import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, resolveImg } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { CategoryBar } from "@/components/CategoryBar";
import { PropertyCard } from "@/components/PropertyCard";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, MapPin, Star } from "lucide-react";

const REGIONS = ["Bran-Moieciu", "Poiana Brașov", "Sinaia", "Zărnești", "Transfăgărășan", "Apuseni", "Maramureș", "Bucegi"];
const HERO = "https://customer-assets-4nw71qhi.emergentagent.net/job_inchirieri-locuinte/artifacts/nagdwrer_aewe.png";

export default function Home() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const [region, setRegion] = useState("all");
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (category !== "all") params.category = category;
      if (region !== "all") params.region = region;
      if (query) params.q = query;
      const { data } = await api.get("/properties", { params });
      setProperties(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [category, region]);

  const scrollToListings = () => document.getElementById("listings")?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="App">
      <Navbar onSearchClick={scrollToListings} />

      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0">
          <img src={HERO} alt="Carpați" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/20 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-28 md:py-40">
          <div className="max-w-2xl animate-fade-up">
            <p className="overline text-white/80 mb-4">Munții Apuseni te vor primi cu brațele deschise. Nu mai trebuie să aștepți altceva</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-tight">
              Cabane de poveste și experiențe autentice în munții României
            </h1>
            <p className="text-lg text-white/85 mt-5 leading-relaxed">
              De la cabane ascunse în pădure, unde găsești tot confortul, dar te și bucuri de natură, la colibe surprinzătoare, amplasate pe malul apei — spații care nu îți vor oferi doar un refugiu unde să-ți încarci bateriile, ci și o experiență autentică, de neuitat.
            </p>
          </div>

          {/* Search bar */}
          <div className="relative mt-10 bg-background rounded-2xl shadow-2xl p-2 flex flex-col md:flex-row gap-2 max-w-3xl animate-fade-up" style={{ animationDelay: "150ms" }}>
            <div className="flex-1 flex items-center gap-3 px-4 py-3">
              <MapPin className="h-5 w-5 text-accent shrink-0" />
              <div className="flex-1">
                <p className="text-xs font-semibold">Destinație</p>
                <Input value={query} onChange={(e) => setQuery(e.target.value)}
                       onKeyDown={(e) => e.key === "Enter" && load()} data-testid="hero-search-input"
                       placeholder="Caută regiune sau cabană..."
                       className="border-0 p-0 h-6 text-sm shadow-none focus-visible:ring-0" />
              </div>
            </div>
            <div className="hidden md:block w-px bg-border my-2" />
            <div className="flex items-center gap-3 px-4 py-3">
              <select value={region} onChange={(e) => setRegion(e.target.value)} data-testid="filter-region-select"
                      className="bg-transparent text-sm font-medium outline-none cursor-pointer">
                <option value="all">Toate regiunile</option>
                {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <Button onClick={load} data-testid="hero-search-button"
                    className="bg-accent hover:bg-accent/90 text-accent-foreground rounded-xl h-auto md:px-6 gap-2">
              <Search className="h-4 w-4" /> Caută
            </Button>
          </div>
        </div>
      </section>

      <CategoryBar active={category} onChange={setCategory} />

      {/* Listings */}
      <section id="listings" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">
              {region === "all" ? "Cazări populare în Apuseni" : `Cazări în ${region}`}
            </h2>
            <p className="text-muted-foreground mt-1">{properties.length} proprietăți disponibile</p>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 lg:gap-8">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="space-y-3">
                <Skeleton className="aspect-[4/3] rounded-2xl" />
                <Skeleton className="h-4 w-3/4" /><Skeleton className="h-4 w-1/2" />
              </div>
            ))}
          </div>
        ) : properties.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-lg text-muted-foreground">Nicio proprietate găsită. Încearcă alte filtre.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 lg:gap-8">
            {properties.map((p, i) => <PropertyCard key={p.id} property={p} index={i} />)}
          </div>
        )}
      </section>

      {/* Experiences teaser */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="rounded-3xl overflow-hidden relative bg-primary text-primary-foreground p-10 md:p-16">
          <div className="relative z-10 max-w-xl">
            <p className="overline text-primary-foreground/70 mb-3">Nu doar cazare</p>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight">
              Descoperă experiențe montane de neuitat
            </h2>
            <p className="text-primary-foreground/80 mt-4 leading-relaxed">
              Drumeții unice și memorabile în Munții Apuseni, explorarea peșterilor cu ghid și echipament ultra-performant, degustare de produse locale direct de la fermele și gospodăriile din zonă, foc de tabără sub cerul liber.
            </p>
            <Button onClick={() => navigate("/experiente")} data-testid="explore-experiences-button"
                    className="mt-6 bg-accent hover:bg-accent/90 text-accent-foreground rounded-full px-6">
              Explorează experiențele
            </Button>
          </div>
          <Star className="absolute -right-8 -bottom-8 h-64 w-64 text-primary-foreground/5" />
        </div>
      </section>

      <Footer />
    </div>
  );
}
